"use client";

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";
import { Icon } from "@iconify/react";

import VideoOptionMenu from "@/data/VideoOptionMenu";
import { socketLike, socketUnLike } from "@/socket";
import SocketContext from "@/socket/Context";
import ShareMenu from "@/data/ShareMenu";
import services from "@/store/services";
import Components from "@/components";
import Partials from "../partials";
import "../player-side.scss";
import sk from "@/skeletons";
import utils from "@/utils";
import api from "@/api";

function InteractionSection(props) {
  const { videoInfo, courseInfo, isEnrolled, isRestricted, articleInfo } =
    useSelector((state) => state.video);
  const [like, setLike] = useState({ status: false, count: 0 });
  const { socket } = useContext(SocketContext).SocketState;
  const user = useSelector((state) => state?.user?.users);
  const { handleWarning, isLoading } = props;

  const dispatch = useDispatch();
  const router = useRouter();

  const enrolledBeforeApril = useMemo(() => {
    const AddOnCourses = [
      "67519e5f97e6f186a86c34e7", //Web Fundamentals
      "675c48e2da37aaa2615133e8", //COmputer Networks
      "67945eb4f6aa8a9b3bbb9483", //Operating System
      "67e3ceca45b2b98d0db4e709", //Low level design
      "64fb4665bf30d08470ece486", //Dynamic Programming
      "680fb364438493edbbcc9b03", //GIT & GITHUB
    ];
    if (!isEnrolled) return false;
    const enrolledDate = new Date(isEnrolled.createdAt);
    const compareDate = new Date("2025-04-01T00:00:00.000Z");
    return (
      enrolledDate <= compareDate ||
      AddOnCourses.includes(isEnrolled?.courseId.toString())
    );
  }, [isEnrolled]);

  useEffect(() => {
    const fetchLikeStatus = async () => {
      let likeStatus = {
        count: videoInfo.likes,
        status: videoInfo?.userLiked ?? false,
      };
      setLike((prev) => ({ ...prev, ...likeStatus }));
    };
    if (videoInfo) fetchLikeStatus();
    return () => {};
  }, [videoInfo]);

  useEffect(() => {
    if (videoInfo) {
      // socket listner to increase the like
      socket?.on("video:likeIncrease", (res) => {
        if (res.videoId === videoInfo._id) {
          setLike((prev) => ({ ...prev, count: prev.count + 1 }));
          // TODO: add animation here to numebr increment
        }
      });

      // socket listner to decrease the like
      socket?.on("video:likeDecrease", (res) => {
        if (res.videoId === videoInfo._id) {
          setLike((prev) => ({ ...prev, count: prev.count - 1 }));
          // TODO: add animation here to numebr increment
        }
      });
    }
  }, [socket, videoInfo]);

  // ------------------------- Video Like -------------------------

  const onLikeEvent = async () => {
    if (user?.email) {
      if (!like.status) {
        socketLike(
          {
            userId: user?._id,
            entityId: videoInfo?._id,
          },
          socket
        );
        setLike((prev) => ({
          status: true,
          count: prev.count + 1,
        }));
      } else {
        socketUnLike(
          {
            userId: user?._id,
            entityId: videoInfo?._id,
          },
          socket
        );
        setLike((prev) => ({
          status: false,
          count: prev.count - 1,
        }));
      }
    } else {
      dispatch(services.toggleSigninModal());
    }
  };

  const triggerAlert = (type, msg) => {
    if (type === "success") dispatch(services.successAlert(msg));
    else if (type === "warning") dispatch(services.warningAlert(msg));
    else if (type === "failure") dispatch(services.failureAlert(msg));
  };
  const onSaveEvent = async () => {
    if (utils.isLogin()) {
      if (!videoInfo?.isWishlisted) {
        let response = await api.addWatchLaterVideo(
          videoInfo?._id,
          courseInfo?._id
        );
        if (response.data?.success === 200) {
          dispatch(services.toggleWishlistStatus(true));
          dispatch(services.successAlert("Added To Wishlist"));
        } else if (response.data?.success === 409)
          dispatch(services.warningAlert("Already in wishlist"));
        else dispatch(services.failureAlert("Already in wishlist!"));
      } else {
        let response = await api.removeWatchLaterVideo(videoInfo?._id);
        if (response.data?.success === 200) {
          dispatch(services.toggleWishlistStatus(false));
          dispatch(services.successAlert("Removed From Wishlist"));
        } else
          dispatch(services.failureAlert("Couldn't remove from wishlist!"));
      }
    } else {
      dispatch(services.toggleSigninModal());
    }
  };
  return (
    <main className="vd-player-details">
      <section className="vd-player-intro">
        <header>
          {(!isLoading && videoInfo) || articleInfo ? (
            <Components.Tooltip title={videoInfo?.title || ""} placement="top">
              <h1>{videoInfo?.title || articleInfo?.title}</h1>
            </Components.Tooltip>
          ) : (
            <>
              <sk.SkeletonElement type="sk-video-head1" />
              <sk.SkeletonElement type="sk-video-head1" />
            </>
          )}
        </header>
        <article>
          {(!isLoading && courseInfo) || videoInfo ? (
            <div className="vd-course-row">
              <Components.Tooltip title={courseInfo?.title} placement="top">
                <h2>Course: {courseInfo?.title}</h2>
              </Components.Tooltip>
              <div className="vd-btns-container">
                {user.email &&
                isEnrolled &&
                ((isEnrolled?.visibility === "restricted" &&
                  isEnrolled?.certificate?.id) ||
                  isEnrolled?.visibility !== "restricted" ||
                  enrolledBeforeApril) ? (
                  !isLoading ? (
                    <Partials.CertificateReqBtn
                      warningCallback={(obj) => handleWarning(obj)}
                    />
                  ) : (
                    <sk.SkeletonElement type="sk-follow-btn" />
                  )
                ) : null}
                {!isRestricted ? (
                  !isLoading ? (
                    <Partials.EnrollUnEnrollBtn
                      warningCallback={(obj) => handleWarning(obj)}
                    />
                  ) : (
                    <sk.SkeletonElement type="sk-follow-btn" />
                  )
                ) : null}
              </div>
            </div>
          ) : (
            <sk.SkeletonElement type="sk-video-head2" />
          )}
        </article>
      </section>
      <section className="vd-extra-detail-row">
        <article className="vd-creator-section">
          <div
            className="vd-creator"
            onClick={() =>
              router.push(`/profile/${videoInfo?.creatorInfo?.[0].username}`)
            }
            id="vd_Creator_Name"
          >
            {!isLoading && (courseInfo || videoInfo) ? (
              <Components.Avatar
                src={
                  courseInfo?.creatorInfo[0]?.profileImage ??
                  videoInfo?.creatorInfo[0]?.profileImage
                }
                alt={
                  courseInfo?.creatorInfo[0]?.name ??
                  videoInfo?.creatorInfo[0]?.profileImage
                }
                loader="lg"
                errStyle={{ fontSize: "1rem" }}
                className={"vd-creator-profile"}
              />
            ) : (
              <sk.SkeletonElement type="sk-small-circle" />
            )}
            <div>
              <h2 className="vd-creator-name">
                {!isLoading && (courseInfo || videoInfo) ? (
                  `${courseInfo?.creatorInfo[0]?.name ?? videoInfo?.creatorInfo[0]?.name}`
                ) : (
                  <sk.SkeletonElement type="sk-name-text" />
                )}
              </h2>
              <p className="vd-creator-followers">
                {!isLoading && videoInfo ? (
                  <>
                    {utils.formatNumber(videoInfo?.creatorInfo[0]?.followers)}{" "}
                    Followers
                  </>
                ) : null}
              </p>
            </div>
          </div>
          {!isLoading && (courseInfo || videoInfo) ? (
            <Components.FollowBtn user={courseInfo?.creatorInfo?.[0]} />
          ) : (
            <sk.SkeletonElement type="sk-follow-btn" />
          )}
        </article>
        <article className="vd-player-tools">
          {/* {!isLoading && Object.keys(user).length !== 0 ? (
            <Link
              to={`/buy-me-coffee?${courseInfo ? "cid" : "vid"}=${
                courseInfo ? courseInfo?._id : videoInfo?._id
              }`}
              className="vd-icon views"
            >
              <Icon icon={"ph:coffee"} />
              {!isLoading ? <p></p> : <sk.SkeletonElement type="sk-text" />}
            </Link>
          ) : isLoading ? (
            <sk.SkeletonElement type="sk-very-small-circle" />
          ) : (
            <Button
              handleClick={() => dispatch(services.toggleSigninModal())}
              icon={<Icon icon={"ph:coffee-fill"} />}
              className="vd-icon views"
            />
          )}{" "} */}
          {articleInfo === null ? (
            <>
              <div className="vd-icon" onClick={onLikeEvent} id="vd_Like_Btn">
                {!isLoading ? (
                  <Icon
                    icon={"bx:like"}
                    className={like.status ? "liked" : ""}
                  />
                ) : (
                  <sk.SkeletonElement type="sk-very-small-circle" />
                )}
                {!isLoading ? (
                  <p>{utils.formatNumber(like.count)}</p>
                ) : (
                  <sk.SkeletonElement type="sk-text" />
                )}
              </div>
              <div className="vd-icon" onClick={onSaveEvent} id="vd_Save_Btn">
                {!isLoading ? (
                  videoInfo?.isWishlisted ? (
                    <Icon
                      icon="ri:menu-add-line"
                      style={{ color: "#f3912e" }}
                    />
                  ) : (
                    <Icon icon={"ri:menu-add-line"} />
                  )
                ) : (
                  <sk.SkeletonElement type="sk-very-small-circle" />
                )}
                {!isLoading ? (
                  <p
                    style={videoInfo?.isWishlisted ? { color: "#f3912e" } : {}}
                  >
                    Save
                  </p>
                ) : (
                  <sk.SkeletonElement type="sk-text" />
                )}
              </div>
            </>
          ) : null}
          <div className="vd-share">
            <div className="vd-icon">
              {!isLoading ? (
                <Icon icon={"ph:share-fat-light"} />
              ) : (
                <sk.SkeletonElement type="sk-very-small-circle" />
              )}
              {!isLoading ? (
                <p>Share</p>
              ) : (
                <sk.SkeletonElement type="sk-text" />
              )}
            </div>
            {!isLoading ? (
              <div className="vd-options-cont">
                <ul>
                  {ShareMenu(window.location.href).map((item, index) => {
                    return (
                      <li
                        key={index}
                        onClick={
                          index === 3
                            ? () => {
                                dispatch(
                                  services.successAlert(
                                    "Link copy successfully!"
                                  )
                                );
                                return item.url();
                              }
                            : () => {}
                        }
                      >
                        <a
                          href={item.id === 3 ? "" : item.url}
                          target="_blank"
                          alt="url link"
                          id={`vd_Share_${item.title}`}
                        >
                          <Icon icon={item.icon} alt="social-icon" />
                          <p>{item.title}</p>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
          <div className="vd-share">
            <div className="vd-icon">
              {!isLoading ? (
                <Icon icon={"tabler:dots"} />
              ) : (
                <sk.SkeletonElement type="sk-very-small-circle" />
              )}
            </div>
            {!isLoading ? (
              <div className="vd-options-cont">
                <ul>
                  {VideoOptionMenu.map((item, index) => {
                    return (
                      <li
                        key={index}
                        onClick={() =>
                          item.clickevent({
                            id: videoInfo?._id,
                            courseid: courseInfo?._id,
                            triggerAlert,
                            onReport: () =>
                              dispatch(services.toggleFeedbackModal()),
                            toggleLoginModal: () =>
                              dispatch(services.toggleSigninModal()),
                          })
                        }
                      >
                        <Icon icon={item.icon} alt="social-icon" />
                        <p>{item.title}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        </article>
      </section>
    </main>
  );
}

export default React.memo(InteractionSection);
